import {
  Plane, Bus, HeartPulse, Briefcase, GraduationCap, BedDouble,
  CalendarDays, ShoppingBag, Home, Ticket, Siren,
  Accessibility, MessageSquare, Ear, Brain, Eye, HandHeart,
  SlidersHorizontal, ShieldCheck, Layers, Lock, UtensilsCrossed,
} from "lucide-react";

// Official primary Aiga Enable brand logo (self-hosted, full quality).
export const LOGO_URL = "/assets/aiga-enable-logo.png";

// Legacy transparent-background mark, retained ONLY for the faint decorative
// watermark inside the A-ID card (the primary logo's white background cannot
// be used as a watermark on a dark surface).
export const LOGO_MARK_URL =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_lived-experience-5/artifacts/257d4ih8_IMG_8328.png";

export const FOUNDER_PHOTO =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_lived-experience-5/artifacts/8lthbu5r_43BC768F-AB3E-4FCC-A7C4-48AC64365813.png";

export const CONTACT = {
  email: "support@aigaenable.co.nz",
  linkedin: "https://www.linkedin.com/company/aiga-enable",
  facebook: "https://www.facebook.com/profile.php?id=61594023716466",
};

// A-ID card QR destination. Points to the public Example A-ID demo page;
// AidCard prefixes the current origin so it works on preview and production.
export const AID_QR_TARGET = "/example-aid";

// Provided demo portrait for the A-ID example card (fictional demo user).
export const AID_DEMO_PHOTO =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_lived-experience-5/artifacts/21it0kmm_IMG_8479.jpeg";

export const NAV_LINKS = [
  { n: "01", label: "Aiga Enable", href: "#about" },
  { n: "02", label: "A-ID", href: "#a-id" },
  { n: "03", label: "How It Works", href: "#how" },
  { n: "04", label: "Community", href: "#community" },
  { n: "05", label: "Our Story", href: "#meaning" },
  { n: "06", label: "Early Access", href: "#contact" },
];

// "How A-ID Works" journeys — 9 confirmed MVP journeys. The central A-ID stays
// constant; `examples` are the real-life accessibility details surfaced per journey.
export const SCROLL_CONTEXTS = [
  {
    id: "travel", label: "Travel", Icon: Plane, color: "#00D2B4", angle: -90,
    examples: [
      { title: "Wheelchair transfers", detail: "Requires a hoist for transfers." },
      { title: "Airport assistance", detail: "Requires assistance from check-in through to boarding." },
      { title: "Mobility equipment", detail: "Travels with a powered wheelchair." },
      { title: "Boarding assistance", detail: "Requires assistance boarding and disembarking from the aircraft." },
    ],
  },
  {
    id: "transport", label: "Transport", Icon: Bus, color: "#3B82F6", angle: -50,
    examples: [
      { title: "Vehicle boarding", detail: "Requires a ramp or lift to board." },
      { title: "Wheelchair space", detail: "Requires a designated wheelchair position." },
      { title: "Transfers", detail: "May require assistance with safe transfers." },
      { title: "Journey information", detail: "Needs clear information about stops, delays and changes." },
    ],
  },
  {
    id: "accommodation", label: "Stay & Accommodation", Icon: BedDouble, color: "#6366F1", angle: -10,
    examples: [
      { title: "Room access", detail: "Requires step-free access to the room." },
      { title: "Accessible bathroom", detail: "Requires an accessible bathroom with sufficient space." },
      { title: "Bed access", detail: "Requires sufficient space beside the bed for transfers." },
      { title: "Emergency access", detail: "Needs accessible emergency evacuation arrangements." },
    ],
  },
  {
    id: "health", label: "Health & Care", Icon: HeartPulse, color: "#14B8A6", angle: 30,
    examples: [
      { title: "Transfers", detail: "Requires a hoist for transfers." },
      { title: "Accessible treatment space", detail: "Requires an accessible examination or treatment space." },
      { title: "Personal care", detail: "May require assistance with personal care." },
      { title: "Support person", detail: "May require a support person to be present." },
    ],
  },
  {
    id: "employment", label: "Employment", Icon: Briefcase, color: "#06B6D4", angle: 70,
    examples: [
      { title: "Workplace access", detail: "Requires step-free access throughout the workplace." },
      { title: "Accessible workspace", detail: "Requires an accessible workstation." },
      { title: "Assistive technology", detail: "May require assistive or adapted technology." },
      { title: "Communication", detail: "May need information provided in an accessible format." },
    ],
  },
  {
    id: "education", label: "Education & Learning", Icon: GraduationCap, color: "#8B5CF6", angle: 110,
    examples: [
      { title: "Classroom access", detail: "Requires an accessible classroom and suitable seating." },
      { title: "Learning materials", detail: "May require information in an accessible format." },
      { title: "Movement between spaces", detail: "Requires accessible routes between learning areas." },
      { title: "Practical activities", detail: "May require reasonable adjustments or additional support." },
    ],
  },
  {
    id: "events", label: "Events & Entertainment", Icon: Ticket, color: "#A855F7", angle: 150,
    examples: [
      { title: "Venue access", detail: "Requires step-free access to the event space." },
      { title: "Accessible seating", detail: "Requires an appropriate wheelchair position." },
      { title: "Accessible facilities", detail: "Needs an accessible bathroom available." },
      { title: "Arrival & assistance", detail: "May require assistance from the entrance to the event space." },
    ],
  },
  {
    id: "dining", label: "Dining & Hospitality", Icon: UtensilsCrossed, color: "#F59E0B", angle: 190,
    examples: [
      { title: "Accessible seating", detail: "Requires sufficient space for a wheelchair." },
      { title: "Table access", detail: "Needs a table with suitable height and clearance." },
      { title: "Accessible bathroom", detail: "Needs an accessible toilet available." },
      { title: "Communication", detail: "May need menus or information provided in an accessible format." },
    ],
  },
  {
    id: "emergency", label: "Emergency & Safety", Icon: Siren, color: "#EF4444", angle: 230,
    examples: [
      { title: "Emergency communication", detail: "May need information communicated clearly and directly." },
      { title: "Mobility assistance", detail: "Requires assistance with emergency movement or evacuation." },
      { title: "Transfers", detail: "Requires appropriate equipment and assistance for safe transfers." },
      { title: "Emergency contacts", detail: "Important support and contact information can be shared when needed." },
    ],
  },
];

export const JOURNEYS = [
  { id: "travel", name: "Travel & Holidays", blurb: "Airports, airlines, hotels and travel experiences.", Icon: Plane, color: "#00D2B4" },
  { id: "transport", name: "Transport", blurb: "Buses, trains, taxis, rideshare and other transport.", Icon: Bus, color: "#10B981" },
  { id: "health", name: "Health & Care", blurb: "Hospitals, clinics, doctors and specialists.", Icon: HeartPulse, color: "#14B8A6" },
  { id: "work", name: "Work", blurb: "Workplaces, employers and recruitment.", Icon: Briefcase, color: "#F59E0B" },
  { id: "education", name: "Education & Learning", blurb: "Schools, colleges, universities and training.", Icon: GraduationCap, color: "#8B5CF6" },
  { id: "accommodation", name: "Accommodation", blurb: "Hotels, short stays, housing and other accommodation.", Icon: BedDouble, color: "#3B82F6" },
  { id: "events", name: "Events", blurb: "Conferences, venues, concerts and gatherings.", Icon: CalendarDays, color: "#6366F1" },
  { id: "services", name: "Services & Shopping", blurb: "Retail, banks, government and other services.", Icon: ShoppingBag, color: "#06B6D4" },
  { id: "everyday", name: "Everyday Life", blurb: "Community, leisure, activities and everyday experiences.", Icon: Home, color: "#22C55E" },
];

export const DIMENSIONS = [
  { label: "Mobility", Icon: Accessibility, color: "#00D2B4" },
  { label: "Communication", Icon: MessageSquare, color: "#38BDF8" },
  { label: "Hearing", Icon: Ear, color: "#818CF8" },
  { label: "Cognitive", Icon: Brain, color: "#F59E0B" },
  { label: "Vision", Icon: Eye, color: "#10B981" },
  { label: "Support", Icon: HandHeart, color: "#EC4899" },
];

export const CONTROL_PRINCIPLES = [
  { title: "Choice", Icon: SlidersHorizontal, desc: "Choose what information is relevant to your situation.", color: "#00D2B4" },
  { title: "Control", Icon: ShieldCheck, desc: "Remain in control of your accessibility identity and information.", color: "#00F0FF" },
  { title: "Context", Icon: Layers, desc: "Share information that is relevant to the journey you are taking.", color: "#3B82F6" },
  { title: "Privacy", Icon: Lock, desc: "Treat personal accessibility information with care and respect.", color: "#8B5CF6" },
];

export const PROBLEM_POINTS = [
  {
    title: "Information is fragmented",
    detail: "Important accessibility information can sit across different systems and organisations, with no consistent way for it to move with the person.",
  },
  {
    title: "People have to repeat themselves",
    detail: "People with disabilities are often left carrying the burden of explaining their accessibility needs again and again.",
  },
  {
    title: "Information can become outdated",
    detail: "Different organisations may hold different versions of a person’s information, including information that is incomplete or no longer accurate.",
  },
  {
    title: "Needs can be misunderstood",
    detail: "When accessibility information is recorded or communicated differently across systems, the support provided may not reflect what the person actually needs.",
  },
  {
    title: "The consequences can be serious",
    detail: "When the right information isn’t available at the right time, people can experience barriers, delays or inappropriate support. In critical situations, missing or incorrect accessibility information can create serious safety risks.",
  },
];

export const ROLE_OPTIONS = [
  "Individual with lived experience",
  "Family member or carer",
  "Accessibility organisation",
  "Technology partner",
  "Funder or investor",
  "Other",
];
